// ----------------------------------------------------------------------
// The app-wide `data-testid` scheme.
//
// Every Playwright selector goes through `getByTestId`. The UI is translated into four locales
// with runtime-fetched JSON, so a text selector only passes in whichever language the author
// happened to be running — that is how the previous suite ended up mostly red.
//
// Form fields derive their ids from the RHF field `name` and are handled separately, in
// `src/components/hook-form/testid.ts`. Do not hand-write `{name}-input` in a view.
//
// The full scheme, with examples, lives in E2E-TESTING.md.
// ----------------------------------------------------------------------

/** Root element of a routed view. `waitForAppReady` waits on this. */
export const pageTestId = (feature: string) => `${feature}-page`;

/**
 * A view's own loading state. `-loading` is a reserved suffix: readiness asserts it is *absent*,
 * so anything wearing it must unmount once the data has arrived.
 */
export const loadingTestId = (root: string) => `${root}-loading`;

/** Empty-result state. Reserved suffix, same contract as `-loading`. */
export const emptyTestId = (feature: string) => `${feature}-empty`;

/** Table / list container. */
export const tableTestId = (feature: string) => `${feature}-table`;

/**
 * One row, keyed by entity id — never by index. An index-keyed id silently points at a different
 * record as soon as sorting or pagination changes, and makes failures unreadable.
 */
export const rowTestId = (feature: string, entityId: string | number) =>
  `${feature}-row-${entityId}`;

/** An action button inside a row, e.g. `students-row-8f21-delete`. */
export const rowActionTestId = (feature: string, entityId: string | number, action: string) =>
  `${feature}-row-${entityId}-${action}`;

/** A card in a grid layout, keyed by entity id for the same reason as rows. */
export const cardTestId = (feature: string, entityId: string | number) =>
  `${feature}-card-${entityId}`;

/** A tab trigger. `value` must match the `?tab=` search param the tab sets. */
export const tabTestId = (feature: string, value: string) => `${feature}-tab-${value}`;

/** Modal / dialog root, e.g. `delete-course-modal`. */
export const modalTestId = (action: string, entity: string) => `${action}-${entity}-modal`;

/** A modal's confirm or cancel button. */
export const modalButtonTestId = (action: string, entity: string, button: 'confirm' | 'cancel') =>
  `${modalTestId(action, entity)}-${button}`;

/** Drawer root, e.g. `submissions-grading-drawer`. */
export const drawerTestId = (feature: string) => `${feature}-drawer`;

/** An item inside a popover menu, e.g. `courses-menu-duplicate`. */
export const menuItemTestId = (feature: string, action: string) => `${feature}-menu-${action}`;

/**
 * A sidebar entry, derived from its route so one edit in `NavItem` covers every item:
 * `/courses` → `nav-courses`, `/analytics/progress/members` → `nav-analytics-progress-members`.
 */
export const navTestId = (path: string) => {
  const slug = path.replace(/^\/+|\/+$/g, '').replace(/\//g, '-');
  return slug ? `nav-${slug}` : 'nav-root';
};
